<template>
  <div class="page-header">
    <b-breadcrumb class="m-0">
      <b-breadcrumb-item
        :to="{
          name: 'DashboardHome',
        }"
      >
        <i class="fa fa-home"></i>
      </b-breadcrumb-item>

      <template v-for="(item, index) in breadcrumbItems">
        <b-breadcrumb-item
          v-if="item.to && index !== breadcrumbItems.length - 1"
          :key="`breadcrumb-${index}-link`"
          :to="item.to"
        >
          {{ item.text }}
        </b-breadcrumb-item>
        <b-breadcrumb-item
          v-else
          :key="`breadcrumb-${index}-active`"
          :active="true"
        >
          {{ item.text }}
        </b-breadcrumb-item>
      </template>
    </b-breadcrumb>
    <slot></slot> 
  </div>
</template>

<script>
import sidebarMapMixin from '@/mixins/Dashboard/sidebarMap'
import PermissionChecker from '@/utils/PermissionChecker'

export default {
  name: 'BreadCrumb',
  mixins: [sidebarMapMixin],
  props: {
    // 额外的面包屑项，例如：['編輯會員資料']
    extraItems: {
      type: Array,
      default: () => []
    },
    // 当前路由名称，如果不提供则使用 $route.name
    routeName: {
      type: String,
      default: null
    },
    // 父级路由名称，用于指定上一层的路由，优先使用此值
    parentRouteName: {
      type: String,
      default: null
    }
  },
  computed: {
    currentRouteName() {
      return this.routeName || this.$route.name
    },
    breadcrumbItems() {
      const items = []
      
      // 从 sidebarMap 中找到当前路由对应的菜单项
      let menuItem = this.findMenuItemByRouteName(this.currentRouteName)
      
      // 如果当前路由不在 sidebarMap 中，优先使用 parentRouteName props
      // 如果没有提供 parentRouteName，则尝试通过 findRelatedMenuItem 查找
      if (!menuItem) {
        if (this.parentRouteName) {
          menuItem = this.findMenuItemByRouteName(this.parentRouteName)
        } else {
          menuItem = this.findRelatedMenuItem(this.currentRouteName)
        }
      }
      
      if (menuItem) {
        // 检查菜单项的权限
        const hasMenuItemPermission = menuItem.checkPermissionAny
          ? this.checkPermissionAny(menuItem.checkPermissionAny)
          : menuItem.checkPermission
          ? this.checkPermission(menuItem.checkPermission)
          : true
        
        // 只有当菜单项有权限时才继续
        if (hasMenuItemPermission) {
          // 找到父级菜单（例如：會員管理中心）
          const parentMenu = this.findParentMenu(menuItem)
          
          if (parentMenu) {
            // 检查父级菜单的权限
            const hasParentMenuPermission = parentMenu.checkPermissionAny
              ? this.checkPermissionAny(parentMenu.checkPermissionAny)
              : parentMenu.checkPermission
              ? this.checkPermission(parentMenu.checkPermission)
              : true
            
            // 只有当父级菜单有权限时才添加
            if (hasParentMenuPermission) {
              // 添加父级菜单文本（例如：會員管理中心）
              items.push({
                text: parentMenu.text,
                active: true
              })
            }
          }
          
          // 添加当前菜单项（例如：會員管理）
          // 检查是否是当前路由，如果是则不需要链接
          const isCurrentRoute = menuItem.to && menuItem.to.name === this.currentRouteName
          items.push({
            text: menuItem.text,
            to: isCurrentRoute ? null : menuItem.to,
            active: isCurrentRoute
          })
        }
      }
      
      // 添加额外的面包屑项
      this.extraItems.forEach((item, index) => {
        const isLastExtraItem = index === this.extraItems.length - 1
        if (typeof item === 'string') {
          items.push({
            text: item,
            active: true
          })
        } else {
          // 处理 to 和 params
          let to = null
          if (!isLastExtraItem && item.to) {
            if (typeof item.to === 'string') {
              // 如果 to 是字符串且有 params，需要转换为对象
              to = item.params 
                ? { name: item.to, params: item.params }
                : item.to
            } else {
              // 如果 to 已经是对象，合并 params
              to = item.params
                ? { ...item.to, params: { ...item.to.params, ...item.params } }
                : item.to
            }
          }
          
          items.push({
            text: item.text,
            to: to,
            active: true
          })
        }
      })
      
      // 确保最后一个项目总是 active 状态，没有链接
      if (items.length > 0) {
        const lastItem = items[items.length - 1]
        lastItem.to = null
        lastItem.active = true
      }
      
      return items
    }
  },
  methods: {
    // 根据路由名称在 sidebarMap 中查找对应的菜单项
    findMenuItemByRouteName(routeName) {
      if (!routeName || !this.sidebarMap) return null
      
      for (const menu of this.sidebarMap) {
        if (menu.items && Array.isArray(menu.items)) {
          for (const item of menu.items) {
            // 检查路由名称是否匹配
            if (item.to && item.to.name === routeName) {
              return item
            }
          }
        }
      }
      
      return null
    },
    
    // 查找菜单项的父级菜单
    findParentMenu(menuItem) {
      if (!this.sidebarMap) return null
      
      for (const menu of this.sidebarMap) {
        if (menu.items && Array.isArray(menu.items)) {
          const found = menu.items.find(item => {
            return item.to && item.to.name === menuItem.to.name
          })
          
          if (found) {
            return menu
          }
        }
      }
      
      return null
    },
    
    // 查找相关的菜单项（用于处理不在 sidebarMap 中的路由）
    // 例如：CustomerDetailEdit -> CustomerList
    findRelatedMenuItem(routeName) {
      if (!routeName || !this.sidebarMap) return null
      
      // 定义路由名称的映射关系
      const routeMappings = {
        'CustomerDetailEdit': 'CustomerList',
        'CustomerDetailView': 'CustomerList',
        'CustomerListImport': 'CustomerList',
      }
      
      const relatedRouteName = routeMappings[routeName]
      if (relatedRouteName) {
        return this.findMenuItemByRouteName(relatedRouteName)
      }
      
      return null
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker;
      return checker.check(permissions);
    },
    checkPermissionAny(permissions) {
      const checker = new PermissionChecker;
      return checker.checkAny(permissions);
    }
  }
}
</script>

<style scoped>
.page-header {
  margin-bottom: 1rem;
}
</style>
